import React, { useState } from 'react';
import {Trees, Scissors, Sprout, Mountain, Construction, Axe, ShieldAlert, X} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const serviceIcons = [Scissors, Axe, Sprout, Trees, Mountain, Construction, ShieldAlert];

const serviceImages = [
  {
    image: "https://static.lumi.new/75/755ed8f572c14741814240147e4d003f.webp",
    images: [
      "https://static.lumi.new/75/755ed8f572c14741814240147e4d003f.webp",
      "https://static.lumi.new/c8/c85a9f16b1bfd54872741742fbe4b69b.webp"
    ]
  },
  {
    image: "https://static.lumi.new/68/68e0eaa172eb4a899b332e0424b68a78.webp",
    images: [
      "https://static.lumi.new/68/68e0eaa172eb4a899b332e0424b68a78.webp",
      "https://static.lumi.new/c7/c70010e5f7bb821a86b243917053ef90.webp"
    ]
  },
  {
    image: "https://static.lumi.new/d5/d517ddfd629b54d74b2f315116161f32.webp",
    images: [
      "https://static.lumi.new/d5/d517ddfd629b54d74b2f315116161f32.webp",
      "https://static.lumi.new/0e/0eab4b90a3c6a54ab78f8bb919def428.webp"
    ]
  },
  {
    image: "https://static.lumi.new/ab/ab7477c1b606ee96718e84c356b97d3f.webp",
    images: [
      "https://static.lumi.new/ab/ab7477c1b606ee96718e84c356b97d3f.webp",
      "https://static.lumi.new/6d/6d3745cad9a9a33a6b595dd923965c38.webp"
    ]
  },
  {
    image: "https://static.lumi.new/d2/d2bb75512a846e7a8e61da6cc296fefa.webp",
    images: [
      "https://static.lumi.new/d2/d2bb75512a846e7a8e61da6cc296fefa.webp",
      "https://static.lumi.new/96/9619c7ae801b255620e4f6dcb807952e.webp"
    ]
  },
  {
    image: "https://static.lumi.new/62/6260539bfff35f039fc7193ee9766b0f.webp",
    images: [
      "https://static.lumi.new/62/6260539bfff35f039fc7193ee9766b0f.webp",
      "https://static.lumi.new/35/359bd0040f286c538362495cc284de6b.webp"
    ]
  },
  {
    image: "https://static.lumi.new/b8/b85fcc1552b9b2677d4f34f9d26dc02e.webp",
    images: [
      "https://static.lumi.new/b8/b85fcc1552b9b2677d4f34f9d26dc02e.webp",
      "https://static.lumi.new/55/5507a1586755952acd73f30f9087da07.webp"
    ]
  }
];

export default function Services() {
  const { t } = useLanguage();
  const [selectedService, setSelectedService] = useState<number | null>(null);

  const services = t('services.items');

  const openModal = (index: number) => {
    setSelectedService(index);
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    setSelectedService(null);
    document.body.style.overflow = "auto";
  };

  const service = selectedService !== null ? services[selectedService] : null;
  const serviceIcon = selectedService !== null ? serviceIcons[selectedService] : null;
  const serviceImageData = selectedService !== null ? serviceImages[selectedService] : null;

  return (
    <>
      <section id="services" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-playfair font-bold text-gray-900 mb-4">
              {t('services.title')}
            </h2>
            <p className="text-lg text-gray-600">
              {t('services.description')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service: any, index: number) => {
              const Icon = serviceIcons[index];
              const imageData = serviceImages[index];
              
              return (
                <div
                  key={index}
                  className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col cursor-pointer"
                  onClick={() => openModal(index)}
                >
                  {imageData.image && (
                    <div className="h-48 overflow-hidden">
                      <img
                        src={imageData.image}
                        alt={service.title}
                        className="w-full h-full transform group-hover:scale-110 transition-transform duration-700 object-cover"
                      />
                    </div>
                  )}
                  <div className="p-8 flex-1 flex flex-col">
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-green-700 transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed flex-1">
                      {service.description}
                    </p>
                    
                    <div className="mt-4 flex items-center gap-2 text-green-700 hover:text-green-600 font-semibold text-sm transition-colors">
                      {t('services.learnMore')}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Modal */}
      {service && serviceImageData && (
        <div
          className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-75 flex items-center justify-center p-4"
          onClick={closeModal}
        >
          <div
            className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-8 py-6 flex items-center justify-between z-10">
              <div className="flex items-center gap-4">
                <h2 className="text-2xl md:text-3xl font-playfair font-bold text-gray-900">
                  {service.title}
                </h2>
              </div>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Fechar"
              >
                <X className="h-6 w-6 text-gray-600" />
              </button>
            </div>

            {/* Content */}
            <div className="p-8">
              {/* Images Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                {serviceImageData.images?.map((img, idx) => (
                  <div key={idx} className="aspect-video rounded-xl overflow-hidden shadow-lg">
                    <img
                      src={img}
                      alt={`${service.title} - Imagem ${idx + 1}`}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                ))}
              </div>

              {/* Description */}
              <div className="prose prose-lg max-w-none">
                <p className="text-gray-700 leading-relaxed whitespace-pre-line text-justify">
                  {service.details}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}